import { useState } from 'react';

const COMPONENTS = [
  {
    type: 'heading',
    name: 'Überschrift (Heading)',
    icon: 'H',
    category: 'Struktur',
    canNest: false,
    description: 'Rendert eine H1-H4 Überschrift mit automatischer ID-Anker-Generierung für Sitemaps und Sprungmarken.',
    props: [
      { name: 'level', type: 'Number (1-4)', default: '2', description: 'Hierarchie-Ebene der Überschrift (H1, H2, H3, H4)' },
      { name: 'text', type: 'String', default: '""', description: 'Textinhalt der Überschrift' },
    ],
    sample: { level: 2, text: 'Beispiel-Überschrift H2' },
  },
  {
    type: 'text',
    name: 'Fließtext (Rich Text)',
    icon: '¶',
    category: 'Inhalt',
    canNest: false,
    description: 'Rendert formatierten Rich-Text mit Fett-, Kursiv-, Unterstreichungs- und Link-Formatierung.',
    props: [
      { name: 'html', type: 'HTML String', default: '""', description: 'HTML-formatiertes Fließtext-Fragment' },
    ],
    sample: { html: '<p>Dies ist ein <strong>formatiertes</strong> Textbeispiel mit einem <a href="#">Link</a>.</p>' },
  },
  {
    type: 'image',
    name: 'Bild & Medien (Image)',
    icon: '🖼️',
    category: 'Medien',
    canNest: false,
    description: 'Einbindung von Bildern aus der Medien-Bibliothek inklusive Alt-Text, Bildunterschrift und Vorschaubild.',
    props: [
      { name: 'mediaId', type: 'ID / String', default: '""', description: 'ID des verknüpften Medien-Objekts' },
      { name: 'objectKey', type: 'String', default: '""', description: 'S3-Speicher-Key der Bilddatei' },
      { name: 'alt', type: 'String', default: '""', description: 'Barrierefreier Alt-Text' },
      { name: 'caption', type: 'String', default: '""', description: 'Optionale Bildunterschrift' },
    ],
    sample: { alt: 'Landschaftsfoto', caption: 'Sonnenuntergang am Meer', objectKey: 'media/sample.jpg' },
  },
  {
    type: 'quote',
    name: 'Zitat (Quote)',
    icon: '❝',
    category: 'Inhalt',
    canNest: false,
    description: 'Hebt Zitate und Hervorhebungen mit Akzentleiste und kursiver Formatierung ab.',
    props: [
      { name: 'html', type: 'HTML String', default: '""', description: 'Zitattext' },
    ],
    sample: { html: '„Design ist nicht nur wie es aussieht, sondern wie es funktioniert.“' },
  },
  {
    type: 'container',
    name: 'Container Block (Nested)',
    icon: '📦',
    category: 'Layout & Verschachtelung',
    canNest: true,
    description: 'Ein verschachtelbarer Layout-Container, der weitere Kind-Komponenten aufnehmen kann.',
    props: [
      { name: 'children', type: 'Block[]', default: '[]', description: 'Array aus untergeordneten Kind-Komponenten' },
      { name: 'layout', type: 'String', default: '"1-col"', description: 'Layout-Spaltenaufteilung' },
    ],
    sample: { children: [{ type: 'heading', props: { level: 3, text: 'Container-Titel' } }] },
  },
  {
    type: 'divider',
    name: 'Trennlinie (Divider)',
    icon: '—',
    category: 'Struktur',
    canNest: false,
    description: 'Fügt eine dezent gestaltete horizontale Trennlinie zwischen Inhaltsabschnitten ein.',
    props: [],
    sample: {},
  },
  {
    type: 'spacer',
    name: 'Abstandshalter (Spacer)',
    icon: '↕',
    category: 'Layout',
    canNest: false,
    description: 'Fügt vertikalen Abstand zwischen Blöcken ein (Klein, Mittel, Groß).',
    props: [
      { name: 'size', type: '"small" | "medium" | "large"', default: '"medium"', description: 'Abstandsgröße (1rem, 2rem, 4rem)' },
    ],
    sample: { size: 'medium' },
  },
  {
    type: 'html',
    name: 'Quellcode (HTML)',
    icon: '</>',
    category: 'Erweitert',
    canNest: false,
    description: 'Direkte Einbindung von benutzerdefiniertem HTML/Embed-Code.',
    props: [
      { name: 'html', type: 'Raw String', default: '""', description: 'Roh-HTML-Code' },
    ],
    sample: { html: '<div style="background:#e0f2fe;padding:1rem;border-radius:8px">Custom HTML Component</div>' },
  },
];

export default function ComponentCatalog() {
  const [selectedType, setSelectedType] = useState('heading');
  const [filterCategory, setFilterCategory] = useState('');

  const selectedComp = COMPONENTS.find((c) => c.type === selectedType) || COMPONENTS[0];

  const filteredComponents = filterCategory
    ? COMPONENTS.filter((c) => c.category === filterCategory)
    : COMPONENTS;

  const categories = Array.from(new Set(COMPONENTS.map((c) => c.category)));

  return (
    <div className="stack gap-lg">
      <section className="card">
        <div className="row between responsive-gap">
          <div>
            <h2>Komponenten-Katalog & Übersicht</h2>
            <p className="muted">
              Übersicht aller 8 verfügbaren CMS-Komponenten/Blöcke, deren Eigenschafts-Schemas und Schachtelungsregeln.
            </p>
          </div>
          <div className="button-row compact">
            <button
              type="button"
              className={`button ${!filterCategory ? 'primary' : 'ghost'} small`}
              onClick={() => setFilterCategory('')}
            >
              Alle ({COMPONENTS.length})
            </button>
            {categories.map((cat) => (
              <button
                type="button"
                key={cat}
                className={`button ${filterCategory === cat ? 'primary' : 'ghost'} small`}
                onClick={() => setFilterCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: '1.25rem', minHeight: '600px' }}>
        {/* Left: Component List */}
        <div className="card stack gap-sm" style={{ maxHeight: '720px', overflowY: 'auto' }}>
          <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>
            Verfügbare Bausteine ({filteredComponents.length})
          </div>
          {filteredComponents.map((comp) => {
            const isSelected = comp.type === selectedType;
            return (
              <div
                key={comp.type}
                onClick={() => setSelectedType(comp.type)}
                style={{
                  padding: '0.85rem 1rem',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  border: isSelected ? '2px solid #2563eb' : '1px solid #e2e8f0',
                  background: isSelected ? 'rgba(37,99,235,0.05)' : '#ffffff',
                  transition: 'all 0.15s ease',
                }}
              >
                <div className="row between align-center">
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>
                    <span style={{ marginRight: '0.5rem' }}>{comp.icon}</span>
                    {comp.name}
                  </div>
                  {comp.canNest && (
                    <span className="badge" style={{ background: '#dbeafe', color: '#1d4ed8', fontSize: '0.7rem' }}>
                      Schachtelbar
                    </span>
                  )}
                </div>
                <div className="muted small" style={{ marginTop: '0.25rem', fontSize: '0.8rem' }}>
                  {comp.category} · type: <code>{comp.type}</code>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right: Component Detail & Playground */}
        <div className="card stack gap-md" style={{ maxHeight: '720px', overflowY: 'auto' }}>
          <div className="row between align-center" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '0.75rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>
                Komponenten-Spezifikation
              </div>
              <h3 style={{ margin: '0.2rem 0 0', fontSize: '1.4rem' }}>
                <span style={{ marginRight: '0.5rem' }}>{selectedComp.icon}</span>
                {selectedComp.name}
              </h3>
            </div>
            <div className="row" style={{ gap: '0.5rem' }}>
              <span className="badge" style={{ background: '#f1f5f9', color: '#475569' }}>
                type: {selectedComp.type}
              </span>
              <span
                className="badge"
                style={{
                  background: selectedComp.canNest ? '#dbeafe' : '#f1f5f9',
                  color: selectedComp.canNest ? '#1d4ed8' : '#64748b',
                }}
              >
                {selectedComp.canNest ? 'Schachtelbar (Nested)' : 'Einzelkomponente'}
              </span>
            </div>
          </div>

          <p>{selectedComp.description}</p>

          {/* Props Schema Table */}
          <div>
            <h4>Eigenschaften-Schema (Props Schema)</h4>
            {selectedComp.props.length === 0 ? (
              <p className="muted small">Diese Komponente benötigt keine zusätzlichen Eigenschaften.</p>
            ) : (
              <div className="table-wrap">
                <table className="table compact" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>Eigenschaft (Prop)</th>
                      <th>Datentyp</th>
                      <th>Standardwert</th>
                      <th>Beschreibung</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedComp.props.map((p) => (
                      <tr key={p.name}>
                        <td>
                          <strong>{p.name}</strong>
                        </td>
                        <td>
                          <code>{p.type}</code>
                        </td>
                        <td>{p.default}</td>
                        <td>{p.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Interactive Rendering Playground */}
          <div>
            <h4>Live-Rendering Vorschau</h4>
            <div
              style={{
                padding: '1.5rem',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                minHeight: '100px',
              }}
            >
              {selectedComp.type === 'heading' && (
                <h2>{selectedComp.sample.text}</h2>
              )}
              {selectedComp.type === 'text' && (
                <div dangerouslySetInnerHTML={{ __html: selectedComp.sample.html }} />
              )}
              {selectedComp.type === 'image' && (
                <figure style={{ margin: 0, textAlign: 'center' }}>
                  <div
                    style={{
                      height: '140px',
                      background: '#cbd5e1',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#475569',
                    }}
                  >
                    🖼️ Bild-Vorschau ({selectedComp.sample.objectKey})
                  </div>
                  <figcaption style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.4rem' }}>
                    {selectedComp.sample.caption}
                  </figcaption>
                </figure>
              )}
              {selectedComp.type === 'quote' && (
                <blockquote style={{ borderLeft: '4px solid #2563eb', paddingLeft: '1rem', fontStyle: 'italic', margin: 0 }}>
                  {selectedComp.sample.html}
                </blockquote>
              )}
              {selectedComp.type === 'container' && (
                <div style={{ border: '2px dashed #94a3b8', padding: '1rem', borderRadius: '8px', background: '#ffffff' }}>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem' }}>📦 Container-Inhalt (Nested Component)</div>
                  <h3>Container-Titel</h3>
                  <p className="muted small">Verschachtelte Komponenten werden hier gerendert.</p>
                </div>
              )}
              {selectedComp.type === 'divider' && <hr style={{ border: 0, height: 1, background: '#cbd5e1' }} />}
              {selectedComp.type === 'spacer' && (
                <div style={{ background: 'rgba(37,99,235,0.1)', border: '1px dashed #2563eb', height: '40px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', color: '#2563eb' }}>
                  Abstandshalter (2rem)
                </div>
              )}
              {selectedComp.type === 'html' && (
                <div dangerouslySetInnerHTML={{ __html: selectedComp.sample.html }} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
